import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { setPricingFromQuota, isAdminTestModeForced } from "../lib/api";
import { useAuth } from "./AuthContext";
import UpgradeModal from "../components/UpgradeModal";
import TopupModal from "../components/TopupModal";

const QuotaContext = createContext(null);
export const useQuota = () => useContext(QuotaContext);

export function QuotaProvider({ children }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [quota, setQuota] = useState(null);
  const [upgrade, setUpgrade] = useState(null); // null | detail object
  const [topupOpen, setTopupOpen] = useState(false);
  const checkedReturn = useRef(false);

  const refreshQuota = useCallback(async () => {
    // setPricingFromQuota: los badges de coste (api.js) usan el ancla de precio y el markup que
    // manda el servidor, no una copia local — así no pueden desfasarse de lo que se cobra de
    // verdad aunque cambien el precio de un plan o el markup desde Admin.
    try { const { data } = await api.get("/quota"); setPricingFromQuota(data); setQuota(data); } catch (e) {}
  }, []);

  useEffect(() => {
    if (user && user.id) refreshQuota();
    else setQuota(null);
  }, [user, refreshQuota]);

  // Celebra los hitos que el SERVIDOR da por pendientes (quota.logros_nuevos) y le avisa de que
  // ya se han enseñado, vía POST /logros/visto.
  //
  // Antes esto se calculaba aquí (cruces de 1/25/50/100 sobre total_videos) y la marca de "ya
  // celebrado" vivía en localStorage. Dos motivos para moverlo entero al servidor: borrar el
  // navegador repetía la felicitación —inofensivo con un toast, pero ahora cada hito PAGA
  // créditos y habría anunciado como nuevo un premio ya cobrado—, y el cliente no puede saber
  // cuánto se ha pagado ni con qué tabla de tramos, que es una regla de negocio.
  const celebrando = useRef(false);
  useEffect(() => {
    const pendientes = quota?.logros_nuevos || [];
    if (!pendientes.length || celebrando.current) return;
    celebrando.current = true;
    // Escalonados: si se juntan dos, apilarlos a la vez es ilegible.
    pendientes.forEach((l, i) => setTimeout(() => {
      toast.success(t("logros.hito", { count: l.hito, creditos: l.creditos }), { duration: 8000 });
    }, i * 900));
    api.post("/logros/visto")
      .then(refreshQuota)
      .catch(() => {})
      .finally(() => { celebrando.current = false; });
  }, [quota?.logros_nuevos, refreshQuota, t]);

  useEffect(() => {
    // kind "no_plan" es EXACTAMENTE el mismo estado que ya enseña la página Paywall entera (ver
    // <Protected> en App.js: quota.plan==="gratis" && sin topup). Abrir el modal aquí ENCIMA
    // apilaba dos selectores de plan idénticos — cerrar el modal solo revelaba la misma tabla
    // debajo. El resto de motivos (quota_exceeded, y plan_required/kind="ai_feature" para quien
    // tiene topup pero sigue en gratis) SÍ necesitan el modal: en esos casos Paywall no se
    // muestra (el usuario tiene plan activo o saldo de recarga) y el modal es el único aviso.
    const onExceed = (e) => {
      if (e.detail?.kind !== "no_plan") setUpgrade(e.detail || {});
      refreshQuota();
    };
    window.addEventListener("quota-exceeded", onExceed);
    return () => window.removeEventListener("quota-exceeded", onExceed);
  }, [refreshQuota]);

  // Retorno desde Stripe Checkout (?checkout=success&session_id=...)
  useEffect(() => {
    if (!user || !user.id || checkedReturn.current) return;
    const params = new URLSearchParams(window.location.search);
    const co = params.get("checkout");
    const sid = params.get("session_id");
    if (!co) return;
    checkedReturn.current = true;
    const clean = () => window.history.replaceState({}, "", window.location.pathname);
    if (co === "cancel") { toast.info("Pago cancelado. No se ha cobrado nada."); clean(); return; }
    if ((co === "success" || co === "topup") && sid) {
      const isTopup = co === "topup";
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            setQuota((q) => ({ ...(q || {}), ...(data.quota || {}) }));
            toast.success(isTopup ? "🎉 ¡Saldo recargado! Tus minutos ya están disponibles." : "🎉 ¡Suscripción activada! Tu nuevo plan ya está disponible.");
            await refreshQuota();
            clean();
            return;
          }
        } catch (e) { /* sigue intentando */ }
        if (attempts < 6) setTimeout(poll, 2000);
        else { toast.info("Estamos confirmando tu pago. Se actualizará en unos minutos."); clean(); }
      };
      toast.info("Confirmando tu pago…");
      poll();
    }
  }, [user, refreshQuota]);

  const openUpgrade = (detail) => setUpgrade(detail || {});
  const closeUpgrade = () => setUpgrade(null);
  const openTopup = () => setTopupOpen(true);
  const closeTopup = () => setTopupOpen(false);

  // Recarga de saldo (pago único) -> Stripe Checkout. Redirige el navegador.
  const topup = async (pack) => {
    const { data } = await api.post("/payments/topup", { pack, origin_url: window.location.origin, force_test: isAdminTestModeForced() });
    if (data.url) window.location.href = data.url;
  };

  // Plan de pago -> Stripe Checkout (suscripción). Redirige el navegador.
  const checkout = async (plan, period = "monthly") => {
    const { data } = await api.post("/payments/checkout", { plan, period, origin_url: window.location.origin, force_test: isAdminTestModeForced() });
    if (data.url) window.location.href = data.url;
  };

  // Tras iniciar sesión, si venía con un plan elegido desde la landing, llevarlo al pago.
  const pendingHandled = useRef(false);
  useEffect(() => {
    if (!user || !user.id || !quota || pendingHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout")) return; // volviendo de Stripe, no reintentar
    const pending = localStorage.getItem("vb_pending_plan");
    if (!pending || !["starter", "creator", "pro"].includes(pending)) return;
    const pendingPeriod = localStorage.getItem("vb_pending_period") || "monthly";
    pendingHandled.current = true;
    localStorage.removeItem("vb_pending_plan");
    localStorage.removeItem("vb_pending_period");
    if (pending === quota.plan) {
      toast.info("Ya tienes este plan activo.");
      return;
    }
    if (quota.has_subscription) {
      // Ya está suscrito a otro plan: no crear una segunda suscripción; abrir gestión.
      setUpgrade({ msg: "Ya tienes una suscripción activa. Para cambiar de plan, gestiona tu suscripción desde aquí." });
      return;
    }
    toast.info("Te llevamos al pago de tu plan…");
    checkout(pending, pendingPeriod).catch(() => toast.error("No se pudo iniciar el pago. Inténtalo desde Ajustes."));
  }, [user, quota]);

  // Cambio de plan (con suscripción activa): subida inmediata / bajada diferida.
  const changePlan = async (plan) => {
    const { data } = await api.post("/payments/change-plan", { plan, origin_url: window.location.origin, force_test: isAdminTestModeForced() });
    await refreshQuota();
    return data;
  };
  const cancelPendingChange = async () => {
    const { data } = await api.post("/payments/cancel-pending-change", {});
    await refreshQuota();
    return data;
  };

  // Bajar a Gratis: diferido a fin de ciclo (vía cambio de plan)
  const downgradeFree = async () => {
    const data = await changePlan("gratis");
    return data;
  };

  // Portal de Stripe para gestionar/cancelar
  const openPortal = async () => {
    const { data } = await api.post("/payments/portal", { origin_url: window.location.origin, force_test: isAdminTestModeForced() });
    if (data.url) window.location.href = data.url;
  };

  return (
    <QuotaContext.Provider value={{ quota, refreshQuota, openUpgrade, checkout, downgradeFree, openPortal, openTopup, topup, changePlan, cancelPendingChange }}>
      {children}
      <UpgradeModal open={!!upgrade} detail={upgrade} onClose={closeUpgrade}
        quota={quota} checkout={checkout} downgradeFree={downgradeFree} openPortal={openPortal}
        changePlan={changePlan} cancelPendingChange={cancelPendingChange} refreshQuota={refreshQuota} />
      <TopupModal open={topupOpen} onClose={closeTopup} topup={topup} />
    </QuotaContext.Provider>
  );
}
